<script>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import { DATE_SORT, CANONICAL_SORT, } from '@/assets/js/src/util/uiTools'
import { mapActions, mapState, } from 'pinia'
import { useResourceListStore, } from '@/assets/js/src/modules/resource/_pinia/resourceList'
import { useResourceStore, } from '@/assets/js/src/modules/resource/_pinia/resource'
import { useBibleRefs, } from '@/assets/js/src/util/composables/useBibleRefs'
import { useGraphicListFilter, } from '@/assets/js/src/modules/resource/_composables/useGraphicListFilter'
import { useComponentIsActive, } from '@/assets/js/src/util/composables/useComponentIsActive'
import { useAppUiStore, } from '@/assets/js/src/pinia/appUi'
import H1Title from '@/assets/js/src/components/overlay/H1Title.vue'
import BibleBookFilter from '@/assets/js/src/components/forms/BibleBookFilter.vue'
import SearchHitCount from '@/assets/js/src/components/forms/SearchHitCount.vue'
import SortOptions from '@/assets/js/src/components/forms/SortOptions.vue'
import GridListContent from '@/assets/js/src/components/overlay/GridListContent.vue'
import { useTranslation, } from '@/assets/js/src/util/composables/useTranslation'
import { useSSRContext, } from 'vue'

export default {
    name: 'GraphicList',
    components: {
        GridListContent,
        SortOptions,
        SearchHitCount,
        BibleBookFilter,
        H1Title,
        Overlay, },
    props: {
        asyncStatus: {
            type: Number,
            default: 1,
        },
        provider: {
            type: String,
            default: '',
        },
    },
    setup () {
        if (import.meta.env.SSR) {
            const ctx = useSSRContext()
            ctx.headers.ttl = 'end-of-day'
        }

        return {
            ...useGraphicListFilter(),
            ...useBibleRefs(),
            ...useComponentIsActive(),
            ...useTranslation(),
            sortEnum: [ DATE_SORT, CANONICAL_SORT, ],
        }
    },
    data () {
        return {
            loadingNext: false,
        }
    },
    computed: {
        ...mapState(useResourceListStore, [
            'resourceItems',
            'page',
            'maxPages',
            'count',
            'availableBooks',
        ]),
        ...mapState(useResourceStore,[
            'resources',
        ]),
        ...mapState(useAppUiStore,[
            'printing',
        ]),
    },
    methods: {
        ...mapActions(useResourceListStore,[
            'getResourceItems',
        ]),
        async getMoreResults () {
            if (this.page === this.maxPages) {
                return
            }
            this.loadingNext = true
            let next = this.page + 1
            await this.getResourceItems({ page: next, provider: this.provider, })
            this.loadingNext = false
            this.$bsa && this.$bsa.event({
                eventCategory: 'load more',
                eventAction: 'Grafiken nachladen',
                eventLabel: next,
            })
        },
    },
}
</script>

<template>
    <overlay
        class-name="bs-picture-list"
        full-width
    >
        <v-row no-gutters>
            <v-col
                cols="12"
                md="6"
                offset-md="3"
            >
                <h1-title title-key="async.resource.graphic.list.title" />

                <bible-book-filter
                    v-model="books"
                    :available-books="availableBooks"
                ></bible-book-filter>

                <div class="d-flex align-center mb-7">
                    <search-hit-count
                        :count="count"
                    ></search-hit-count>

                    <v-spacer></v-spacer>

                    <sort-options
                        v-model="sort"
                        :types="sortEnum"
                    ></sort-options>
                </div>
            </v-col>
        </v-row>

        <grid-list-content
            :items="resourceItems"
            :async-status="asyncStatus"
            :page="page"
            :max-pages="maxPages"
            no-items-key="async.resource.graphic.list.no_filtered_items"
            @load-more="getMoreResults"
        >
            <template #item="{item: picItem, index,}">
                <router-link
                    :class="[{'first': index === 0}, 'bs-picture-list--grid__item', 'elevation-1']"
                    :dir="resources[picItem.resource.id].language.rtl ? DIR_RTL : DIR_LTR"
                    :to="{path:`/graphic/${encodeURIComponent(resources[picItem.resource.id].abbreviation)}/${encodeURIComponent(picItem.title)}/${picItem.id}`}"
                >
                    <div
                        class="bs-picture-list--grid__item--limiter"
                    >
                        <bs-v-img
                            :src="picItem.metadata.thumb"
                            :lazy-src="picItem.metadata.lazy_thumb"
                            :alt="picItem.title"
                            :class="['secondary','lighten-3']"
                            aspect-ratio="1"
                        >
                            <template #placeholder>
                                <v-row
                                    class="fill-height ma-0"
                                    align="center"
                                    justify="center"
                                >
                                    <v-progress-circular
                                        indeterminate
                                        :class="['secondary--text']"
                                    ></v-progress-circular>
                                </v-row>
                            </template>
                        </bs-v-img>
                        <h2 class="text-truncate">
                            {{ picItem.title }}
                        </h2>
                    </div>
                </router-link>
            </template>
        </grid-list-content>
    </overlay>
</template>

<style lang="scss">
.bs-app {
    .bs-picture-list {
        color: var(--bs-color-black);

        &--grid {
            margin: 0 -#{map-deep-get($bs-xl, overlay, grid, margin)};
            padding: 0 map-deep-get($bs-xl, body, paddingX);

            @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                padding: 0;
            }

            &__item {
                display: block;
                margin: map-deep-get($bs-xl, overlay, grid, margin);
                overflow: hidden;
                text-decoration: none;
                background-color: var(--bs-color-white);

                h2 {
                    margin: 8px 20px;
                    color: var(--bs-color-black);
                    font-weight: 500;
                    font-size: 18px;
                }

                &:hover, &:focus {
                    h2 {
                        font-weight: 700;
                    }
                }
            }

            &__loading-dummy {
                width: 100%;

                .loading-dummy-item {
                    padding-bottom: 100%;
                }

                .mugen-scroll {
                    width: 100%;
                }
            }
        }
    }
}
</style>
